import { Box, CircularProgress, Divider } from "@mui/joy";
import { useNavigate, useParams } from "react-router-dom";
import { useState, useEffect } from "react";

import { useSupplier } from "../../../../hooks/query";
import { VerticalTabs, SupplierDetails, SupplierInventory, SupplierLaborRates } from "./components";
import Breadcrumb from "./components/Breadcrumb";
import { Alert } from "../../../../shared";
import type { ISupplier } from "../../../../interfaces";

export default function Edit() {
    const navigate = useNavigate();
    const { supplierId } = useParams<{ supplierId: string }>();
    const { useList, useUpdate, useDelete } = useSupplier();
    const { data, isLoading, isError, error } = useList();
    const updateSupplierSvc = useUpdate();
    const deleteSupplierSvc = useDelete();

    const [activeTab, setActiveTab] = useState<string>("details");
    const [supplier, setSupplier] = useState<ISupplier | null>(null);

    // Find the supplier from the list
    useEffect(() => {
        if (data && supplierId) {
            const suppliers = (data as ISupplier[]) || [];
            const found = suppliers.find((s) => s.Id === supplierId);
            if (found) {
                setSupplier(found);
            }
        }
    }, [data, supplierId]);

    async function handleUpdate(updatedData: Partial<ISupplier>) {
        if (!supplierId || !supplier) return;

        try {
            const params = {
                Id: supplierId,
                Name: updatedData.Name,
                Description: updatedData.Description,
                Email: updatedData.Email ?? undefined,
                PhoneNumber: updatedData.PhoneNumber ?? undefined,
                Address: updatedData.Address ?? undefined,
                City: updatedData.City ?? undefined,
                Country: updatedData.Country ?? undefined,
                Website: updatedData.Website ?? undefined,
                LogoUrl: updatedData.LogoUrl ?? undefined,
                Type: updatedData.Type ?? undefined,
            };
            await updateSupplierSvc.mutateAsync(params);
            // Refresh the list to get updated data
            // The query will automatically refetch
        } catch (err) {
            console.error("Failed to update supplier:", err);
        }
    }

    async function handleDelete() {
        if (!supplierId) return;

        try {
            await deleteSupplierSvc.mutateAsync(supplierId);
            navigate("/suppliers/list");
        } catch (err) {
            console.error("Failed to delete supplier:", err);
        }
    }

    if (isLoading) {
        return (
            <Box flex={1} display="flex" justifyContent="center" alignItems="center">
                <CircularProgress />
            </Box>
        );
    }

    if (isError) {
        return (
            <Box flex={1} p={2}>
                <Alert errorMessage={error instanceof Error ? error.message : "Failed to load supplier"} />
            </Box>
        );
    }

    if (!supplier) {
        return (
            <Box flex={1} p={2}>
                <Alert errorMessage="Supplier not found" />
            </Box>
        );
    }

    return (
        <Box
            flex={1}
            display={"flex"}
            flexDirection={"column"}
            maxHeight={"100%"}
            sx={{ overflow: "hidden", minWidth: 0 }}
        >
            <Breadcrumb />
            <Divider sx={{ mt: 0, ml: 0 }} />
            <Box
                component="div"
                width="100%"
                height="100%"
                display="flex"
                flex={1}
                sx={{
                    overflow: "hidden",
                    minWidth: 0,
                }}
            >
                <VerticalTabs value={activeTab} onChange={setActiveTab} supplierType={supplier.Type} />
                <Box
                    flex={1}
                    sx={{
                        overflow: "auto",
                        minWidth: 0,
                    }}
                >
                    {activeTab === "details" && (
                        <SupplierDetails
                            supplier={supplier}
                            onUpdate={handleUpdate}
                            onDelete={handleDelete}
                            isUpdating={updateSupplierSvc.isPending}
                            isDeleting={deleteSupplierSvc.isPending}
                            updateError={updateSupplierSvc.isError ? updateSupplierSvc.error : undefined}
                        />
                    )}
                    {activeTab === "inventory" && <SupplierInventory supplierId={supplier.Id} />}
                    {activeTab === "labor" && <SupplierLaborRates supplierId={supplier.Id} />}
                </Box>
            </Box>
        </Box>
    );
}
